// =====================================================
// COMPOSANT: KpiDetailModal — détail d'une carte KPI (issue #228)
// =====================================================
// Ouvert au clic sur n'importe quelle carte des « Indicateurs globaux ». Liste
// les lignes EXACTEMENT comptées par la carte (mêmes prédicats que compute.ts,
// cf. lib/dashboard-stats/kpi-detail.ts) : le total de la modale doit égaler la
// valeur affichée sur la carte. Suit le pattern de UserCallsDetailModal (Modal +
// EnterpriseTable + pagination locale).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowUpRight, ExternalLink, Inbox, TriangleAlert } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EnterpriseTable } from '@/components/tables/EnterpriseTable';
import {
  buildCrewsListLink,
  buildKpiDetailQuery,
  isCrewsListLinkExact,
  KPI_DETAIL_COLUMN_LABELS,
  KPI_DETAIL_LIMIT,
  KPI_DESCRIPTIONS,
  KPI_LABELS,
  type KpiDetailContext,
  type KpiDetailRow,
  type KpiKey,
} from '@/lib/dashboard-stats';
import { useKpiDetail } from '@/hooks/useKpiDetail';
import { CREW_STATUS_LABELS } from '@/types/crew';
import { PAYMENT_METHOD_LABELS } from '@/types/payment';
import type { CrewStatus } from '@/types/crew';
import type { PaymentMethod } from '@/types/payment';

const PAGE_SIZE = 10;

interface KpiDetailModalProps {
  /** Carte cliquée. `null` = modale fermée. */
  kpiKey: KpiKey | null;
  onClose: () => void;
  context: KpiDetailContext;
  /** Valeur affichée sur la carte — sert de contrôle face au total requêté. */
  cardValue: number;
  /** Libellé de la plage de dates (ex. « 26/05/2026 → 26/08/2026 »). */
  rangeLabel: string;
  /** Libellé de l'édition N (ex. « Édition 2027 »). */
  editionLabel: string;
}

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatAmount(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

/** Traduit un statut brut : statut d'équipage, moyen de paiement, ou tel quel. */
function statusLabel(raw: string): string {
  return (
    CREW_STATUS_LABELS[raw as CrewStatus] || PAYMENT_METHOD_LABELS[raw as PaymentMethod] || raw
  );
}

export function KpiDetailModal({
  kpiKey,
  onClose,
  context,
  cardValue,
  rangeLabel,
  editionLabel,
}: KpiDetailModalProps) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  // Mémoïsé sur les valeurs primitives : useKpiDetail dépend de l'identité de
  // l'objet, un nouveau descripteur à chaque render relancerait la requête.
  const query = useMemo(
    () => (kpiKey ? buildKpiDetailQuery(kpiKey, context) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kpiKey, context.editionId, context.window.start.getTime(), context.window.end.getTime()]
  );

  const { rows, total, loading, error, loadedKey } = useKpiDetail(query);
  // Le résultat en état correspond-il à la carte ouverte ? (cf. loadedKey)
  const ready = loadedKey === kpiKey && !loading;

  useEffect(() => {
    setCurrentPage(1);
  }, [kpiKey]);

  const source = query?.source ?? 'crews';
  const labels = KPI_DETAIL_COLUMN_LABELS[source];

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage]
  );

  const columns = useMemo<ColumnDef<KpiDetailRow>[]>(() => {
    const base: ColumnDef<KpiDetailRow>[] = [
      {
        id: 'created_at',
        header: 'Date',
        cell: ({ row }) => (
          <span className="text-sm text-zinc-600">{formatDateTime(row.original.createdAt)}</span>
        ),
      },
      {
        id: 'primary',
        header: labels.primary,
        cell: ({ row }) => (
          <span className="text-sm font-medium text-zinc-900">{row.original.primary}</span>
        ),
      },
      {
        id: 'secondary',
        header: labels.secondary,
        cell: ({ row }) =>
          row.original.secondary ? (
            <span className="text-sm text-zinc-600">{row.original.secondary}</span>
          ) : (
            <span className="text-sm text-zinc-400">—</span>
          ),
      },
      {
        id: 'status',
        header: labels.status,
        cell: ({ row }) =>
          row.original.status ? (
            <Badge variant="outline">{statusLabel(row.original.status)}</Badge>
          ) : (
            <span className="text-sm text-zinc-400">—</span>
          ),
      },
    ];

    if (source === 'payments') {
      base.push({
        id: 'amount',
        header: 'Montant',
        cell: ({ row }) =>
          row.original.amountCents == null ? (
            <span className="text-sm text-zinc-400">—</span>
          ) : (
            <span className="text-sm tabular-nums text-zinc-900">
              {formatAmount(row.original.amountCents)}
            </span>
          ),
      });
    }

    base.push({
      id: 'open',
      header: '',
      cell: ({ row }) =>
        row.original.href ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onClose();
              navigate(row.original.href!);
            }}
          >
            Ouvrir
            <ArrowUpRight className="ml-1 h-3 w-3" />
          </Button>
        ) : null,
    });

    return base;
  }, [labels, source, navigate, onClose]);

  if (!kpiKey) return null;

  const listLink = buildCrewsListLink(kpiKey, context);
  const listLinkExact = isCrewsListLinkExact(kpiKey);
  const truncated = total > rows.length;
  // Garde-fou anti-régression : si le détail et la carte divergent, on le DIT
  // plutôt que de laisser l'utilisateur croire à un chiffre faux (c'est
  // exactement le reproche de l'issue #228 sur les cases non cliquables).
  const mismatch = !!query && ready && !error && total !== cardValue;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${KPI_LABELS[kpiKey]} — ${KPI_DESCRIPTIONS[kpiKey]}`}
      size="5xl"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-zinc-500">
            {kpiKey === 'pr' ? 'Tous produits' : editionLabel} · {rangeLabel}
          </p>
          <Badge variant="outline" className="tabular-nums">
            {ready ? `${total} ligne${total > 1 ? 's' : ''}` : '…'}
          </Badge>
        </div>

        {mismatch ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Le détail renvoie {total} ligne(s) alors que la carte affiche {cardValue}. Signalez
              cet écart : les deux doivent être calculés sur le même périmètre.
            </span>
          </div>
        ) : null}

        {!query ? (
          // Inatteignable via l'UI (sans édition, `kpis` est null et aucune carte
          // n'est rendue) — mais un spinner éternel serait le pire des symptômes
          // si cette garantie sautait un jour.
          <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
            <Inbox className="mb-3 h-10 w-10 text-zinc-300" />
            <p className="text-sm">Sélectionnez une édition pour consulter le détail.</p>
          </div>
        ) : error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            Erreur de chargement du détail : {error}
          </div>
        ) : !ready ? (
          <div className="flex items-center justify-center gap-2 py-16 text-zinc-500">
            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-zinc-900" />
            <span>Chargement…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
            <Inbox className="mb-3 h-10 w-10 text-zinc-300" />
            <p className="text-sm">Aucune ligne sur ce périmètre.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="max-h-96 overflow-auto" data-testid="kpi-detail-table">
              <EnterpriseTable
                data={paginatedRows}
                columns={columns}
                pageSize={PAGE_SIZE}
                currentPage={safePage}
                manualPagination
              />
            </div>

            {truncated ? (
              <p className="text-xs text-zinc-500">
                {KPI_DETAIL_LIMIT} lignes les plus récentes affichées sur {total}. Affinez la plage
                de dates pour voir le reste.
              </p>
            ) : null}

            {totalPages > 1 ? (
              <div className="flex items-center justify-between border-t border-zinc-200 pt-3 text-sm">
                <span className="text-zinc-500" data-testid="kpi-detail-total">
                  {rows.length} ligne{rows.length > 1 ? 's' : ''} affichée
                  {rows.length > 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-zinc-600">
                    Page {safePage} / {totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    Précédent
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {listLink ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-200 pt-4">
            <p className="text-xs text-zinc-500">
              {listLinkExact
                ? 'La liste équipages ouvrira le même périmètre.'
                : 'La liste équipages sera filtrée sur l’édition et la période, sans le filtre de statut : elle sera donc plus large que ce détail.'}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onClose();
                navigate(listLink);
              }}
            >
              <ExternalLink className="mr-1 h-3 w-3" />
              Ouvrir la liste équipages
            </Button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export default KpiDetailModal;
